import {makeAutoObservable} from "mobx";
import {KeycloakProfile} from "keycloak-js";
import {AttributedKeycloakProfile, AuthService} from "../service/auth.service";
import {AppStore} from "./app.store";
import api from "../api";
import {MyAccountView} from "../types/modules/user/modules";
import {ResponseTypeEnum} from "../types/enums";


export class AuthStore {
    private _authenticated: boolean = false;
    private _profile: KeycloakProfile|null = null;
    private _token: string|undefined;
    private _myAccount: MyAccountView|null = null;

    constructor(
        private readonly appStore: AppStore,
        private readonly authService: AuthService
    ) {
        makeAutoObservable(this);
    }

    get authenticated() {
        return this._authenticated;
    }

    set authenticated(authenticated) {
        this._authenticated = authenticated;
    }

    get profile() {
        return this._profile;
    }

    set profile(profile: KeycloakProfile|null) {
        this._profile = profile;
    }

    get token() {
        return this._token;
    }

    set token(value) {
        this._token = value;
    }

    public async injectAuthenticationToken () {
        return await new Promise((resolve, reject) => {
            //initialize axios request interceptor for Bearer authentication token
            api.interceptors.request.use((config) => {
                config.headers['Authorization'] = `Bearer ${this.token}`;
                config.headers['Locale'] = this.appStore.locale;

                if (process.env.REACT_APP_API_ENV === 'dev') {
                    if (document.cookie.indexOf('XDEBUG_SESSION') !== -1) {
                        config.url = config.url + (config.url?.indexOf('?') !== -1 ? '&': '?') + "XDEBUG_SESSION_START"
                    }
                }
                return config;
            },
            (error) => {
                return reject(error);
            });

            resolve(true);
        })
    }

    public async injectRequestHandler () {
        return await new Promise((resolve, reject) => {
            //initialize axios request interceptor for Bearer authentication token
            api.interceptors.response.use((response) => {
                return response;
            }, (error) => {
                const statusCode = error.response ? error.response.status : null;
                this.appStore.setResponseMessage(`global.error.${ statusCode }`, ResponseTypeEnum.ERROR)
                throw error;
            });

            resolve(true);
        })
    }

    public login = async () => {
        const profile = await this.authService.login();

        if (profile) {
            await this.setAuthenticated(profile)
        }
    }

    public manualRefresh = async () => {
        await this.refresh();
        await this.appStore.setInactive(false);
    }

    public refresh = async () => {
        await this.authService.refreshAccessToken()
            .then(async (refreshed) => {
                if (refreshed) {
                    const profile = await this.authService.loadUserProfile();
                    if (profile) {
                        await this.setAuthenticated(profile);
                    }
                }
            });
    }

    private setAuthenticated = async (profile: AttributedKeycloakProfile) => {
        if (!localStorage.getItem('locale')) {
            localStorage.setItem('locale', profile.attributes.locale[0]);
        }
        this.appStore.locale = localStorage.getItem('locale') as string;
        this.token = this.authService.keyCloak.token;
        await this.injectAuthenticationToken();
        await this.injectRequestHandler();
        await this.appStore.updateApplicationTranslations();
        this.authenticated = true;
        this.profile = profile;
    }

    public logout = () => {
        return this.authService.logout();
    }

    get account(): MyAccountView | null {
        return this._myAccount;
    }

    set account(value: MyAccountView | null) {
        this._myAccount = value;
    }

    setMyAccount = (value: MyAccountView | null) =>  {
        this.account = value;
    }
}